import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type CycleData,
  type CycleRecord,
  cycleError,
  cycleLastMonday,
  cycleStartMonday,
  DEFAULT_CYCLE_LENGTH,
  defaultCycleStart,
  isoWeekAtMonday,
} from "@/lib/cycles";
import { addDays, isoWeekMonday, isoWeekOf, weekRangeLabel } from "@/lib/days";
import { createCycle, updateCycle } from "@/lib/repos/cycles";
import { Modal } from "./Modal";

// How far back and ahead the start-week list reaches, in weeks. Back, so a
// cycle can be set up after the fact; ahead, for planning the next one.
const WEEKS_BACK = 26;
const WEEKS_AHEAD = 52;

/**
 * Mounted only while open, so the form starts fresh from `cycle` or the
 * defaults each time — and from a `cycles` list the parent has already
 * loaded, which the default start week depends on.
 */
interface Props {
  onClose: () => void;
  /** Every cycle, archived ones included. */
  cycles: CycleRecord[];
  /** Absent: «Ny syklus». Present: edit that cycle. */
  cycle?: CycleRecord;
}

function weekKey(year: number, weekNumber: number): string {
  return `${year}-${weekNumber}`;
}

function mondayOfKey(key: string): string {
  const [year, weekNumber] = key.split("-").map(Number);
  return isoWeekMonday(year, weekNumber);
}

function keyOfMonday(monday: string): string {
  const week = isoWeekAtMonday(monday);
  return weekKey(week.year, week.weekNumber);
}

/** Whole weeks from one Monday through another, both included. */
function weeksSpanned(firstMonday: string, lastMonday: string): number {
  const days =
    (Date.parse(`${lastMonday}T00:00:00Z`) -
      Date.parse(`${firstMonday}T00:00:00Z`)) /
    86400000;
  return Math.round(days / 7) + 1;
}

export function CycleModal({ onClose, cycles, cycle }: Props) {
  const navigate = useNavigate();
  const others = useMemo(
    () => cycles.filter((c) => c.id !== cycle?.id).map((c) => c.data),
    [cycles, cycle?.id],
  );

  const [start, setStart] = useState(() => {
    if (cycle) return weekKey(cycle.data.startYear, cycle.data.startWeekNumber);
    const first = defaultCycleStart(others, new Date());
    return weekKey(first.year, first.weekNumber);
  });
  // The last week, not a count: a cycle usually runs up to a date — «fram
  // til jul» — and its length follows from that.
  const [end, setEnd] = useState(() =>
    keyOfMonday(
      addDays(
        mondayOfKey(start),
        ((cycle?.data.length ?? DEFAULT_CYCLE_LENGTH) - 1) * 7,
      ),
    ),
  );
  const [name, setName] = useState(cycle?.data.name ?? "");
  const [note, setNote] = useState(cycle?.data.note ?? "");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const options = useMemo(() => {
    const today = isoWeekOf(new Date());
    const thisMonday = isoWeekMonday(today.year, today.weekNumber);
    const weeks = Array.from(
      { length: WEEKS_BACK + WEEKS_AHEAD + 1 },
      (_, i) => {
        const week = isoWeekAtMonday(addDays(thisMonday, (i - WEEKS_BACK) * 7));
        const taken = others.some(
          (o) =>
            cycleStartMonday(o) <= week.monday &&
            week.monday <= cycleLastMonday(o),
        );
        return { ...week, taken };
      },
    );
    // A cycle being edited may lie outside the window; keep it choosable.
    if (cycle) {
      for (const monday of [
        cycleStartMonday(cycle.data),
        cycleLastMonday(cycle.data),
      ]) {
        if (!weeks.some((w) => w.monday === monday)) {
          weeks.push({ ...isoWeekAtMonday(monday), taken: false });
        }
      }
      weeks.sort((a, b) => (a.monday < b.monday ? -1 : 1));
    }
    return weeks;
  }, [others, cycle]);

  const [startYear, startWeekNumber] = start.split("-").map(Number);
  const startMonday = mondayOfKey(start);
  const length = weeksSpanned(startMonday, mondayOfKey(end));
  const endOptions = options.filter((o) => o.monday >= startMonday);

  const changeStart = (key: string) => {
    setStart(key);
    // Keep the last week unless the new start passes it.
    if (mondayOfKey(key) > mondayOfKey(end)) {
      setEnd(
        keyOfMonday(addDays(mondayOfKey(key), (DEFAULT_CYCLE_LENGTH - 1) * 7)),
      );
    }
  };

  const draft: CycleData = {
    startYear,
    startWeekNumber,
    length,
    name,
    note,
    archived: cycle?.data.archived ?? false,
    targets: cycle?.data.targets ?? [],
  };
  const validation = cycleError(draft, others);
  const error = validation ?? saveError;

  const save = async () => {
    if (validation || submitting) return;
    setSubmitting(true);
    setSaveError(null);
    try {
      if (cycle) {
        await updateCycle(cycle.id, (prev) => ({
          ...prev,
          startYear,
          startWeekNumber,
          length,
          name: name.trim(),
          note: note.trim(),
        }));
      } else {
        const created = await createCycle({
          startYear,
          startWeekNumber,
          length,
          name,
        });
        navigate(`/syklus/${encodeURIComponent(created.id)}`);
      }
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onClose} data-testid="cycle-modal">
      <h2>{cycle ? "Rediger syklus" : "Ny syklus"}</h2>
      <div className="cycle-form">
        <label className="setting-label" htmlFor="cycle-start">
          Første uke
        </label>
        <select
          id="cycle-start"
          className="input"
          value={start}
          onChange={(e) => changeStart(e.target.value)}
          data-testid="cycle-start-select"
        >
          {options.map((o) => (
            <option
              key={o.monday}
              value={weekKey(o.year, o.weekNumber)}
              disabled={o.taken}
            >
              Uke {o.weekNumber}, {o.year} · {weekRangeLabel(o.monday)}
              {o.taken ? " · i en annen syklus" : ""}
            </option>
          ))}
        </select>

        <label className="setting-label" htmlFor="cycle-end">
          Siste uke{" "}
          <small data-testid="cycle-length-hint">
            {length === 1 ? "1 uke" : `${length} uker`}
          </small>
        </label>
        <select
          id="cycle-end"
          className="input"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          data-testid="cycle-end-select"
        >
          {endOptions.map((o) => (
            <option
              key={o.monday}
              value={weekKey(o.year, o.weekNumber)}
              disabled={o.taken}
            >
              Uke {o.weekNumber}, {o.year} · {weekRangeLabel(o.monday)}
              {o.taken ? " · i en annen syklus" : ""}
            </option>
          ))}
        </select>

        <label className="setting-label" htmlFor="cycle-name">
          Navn <small>Valgfritt.</small>
        </label>
        <input
          id="cycle-name"
          className="input"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="cycle-name-input"
        />

        {cycle ? (
          <>
            <label className="setting-label" htmlFor="cycle-note">
              Notat <small>Hvorfor denne syklusen. Valgfritt.</small>
            </label>
            <textarea
              id="cycle-note"
              className="input"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="cycle-note-input"
            />
          </>
        ) : null}
      </div>
      {error ? (
        <p className="error" data-testid="cycle-error">
          {error}
        </p>
      ) : null}
      <div className="modal-actions">
        <button
          type="button"
          className="btn ghost"
          onClick={onClose}
          data-testid="cycle-cancel-button"
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          disabled={validation !== null || submitting}
          data-testid="cycle-save-button"
        >
          {cycle ? "Lagre" : "Opprett"}
        </button>
      </div>
    </Modal>
  );
}
